/**
 * Shared designer grid geometry — grid-only, no pixels stored.
 *
 * This is the Page Designer grid contract with the bounds made a parameter.
 * Both designers consume it, so overlap, containment, clamping, whole-cell
 * move/resize and placement search exist in one place:
 *   - Page Designer (lib/pageDesignerLayout.js) binds it to one fixed 60×40
 *     page, where every card is a peer.
 *   - Form Designer (lib/formDesigner.js) binds it per card, where a control's
 *     peers are only the controls in the same card.
 *
 * An item is anything with integer `col, row, width, height` — 1-based cells.
 * Bounds are `{ columns, rows }`. Pixels appear only where a pointer has to be
 * translated into cells (cellDeltaFromPixels, cellSizeFromRect) and are never
 * written back onto an item.
 *
 * Every mutation returns a NEW array, or null when the change is invalid. A
 * caller can therefore treat null as "not allowed" and keep the last good
 * layout, which is what makes snap-to-grid feel predictable instead of lossy.
 */

export const MIN_ITEM_SIZE = 1;

export function asInt(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clampInt(value, low, high) {
  return Math.min(Math.max(low, value), high);
}

/** Items only ever occupy whole cells, so col/row/width/height compare directly. */
export function itemsOverlap(a, b) {
  if (!a || !b || a.id === b.id) return false;
  return (
    a.col < b.col + b.width &&
    a.col + a.width > b.col &&
    a.row < b.row + b.height &&
    a.row + a.height > b.row
  );
}

export function hasOverlap(items) {
  const list = items || [];
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (itemsOverlap(list[i], list[j])) return true;
    }
  }
  return false;
}

/** The containment invariant: an item never leaves its bounds, on any edge. */
export function isInsideGrid(item, bounds) {
  if (!item || !bounds) return false;
  return (
    item.col >= 1 &&
    item.row >= 1 &&
    item.width >= MIN_ITEM_SIZE &&
    item.height >= MIN_ITEM_SIZE &&
    item.col + item.width - 1 <= bounds.columns &&
    item.row + item.height - 1 <= bounds.rows
  );
}

export function clampMove(item, bounds) {
  const width = Math.max(MIN_ITEM_SIZE, asInt(item.width, MIN_ITEM_SIZE));
  const height = Math.max(MIN_ITEM_SIZE, asInt(item.height, MIN_ITEM_SIZE));
  return {
    ...item,
    width: Math.min(width, bounds.columns),
    height: Math.min(height, bounds.rows),
    col: clampInt(asInt(item.col, 1), 1, Math.max(1, bounds.columns - width + 1)),
    row: clampInt(asInt(item.row, 1), 1, Math.max(1, bounds.rows - height + 1)),
  };
}

export function clampResize(item, bounds) {
  const col = clampInt(asInt(item.col, 1), 1, bounds.columns);
  const row = clampInt(asInt(item.row, 1), 1, bounds.rows);
  return {
    ...item,
    col,
    row,
    width: clampInt(asInt(item.width, MIN_ITEM_SIZE), MIN_ITEM_SIZE, bounds.columns - col + 1),
    height: clampInt(asInt(item.height, MIN_ITEM_SIZE), MIN_ITEM_SIZE, bounds.rows - row + 1),
  };
}

/** Repairs any item into valid geometry. Never fails, never drops an item. */
export function clampItem(item, bounds) {
  return clampResize(clampMove(item, bounds), bounds);
}

export function findItem(items, id) {
  return (items || []).find((item) => item.id === id) || null;
}

/**
 * Applies a patch when the result stays inside `bounds` and clear of every
 * `peers` item. `peers` is what the item shares a grid with — for a control
 * that is the other controls in the same card, not the whole form.
 */
export function tryUpdateItem(items, id, patch, { bounds, peers, mode = "move" } = {}) {
  const current = findItem(items, id);
  if (!current) return null;
  const merged = { ...current, ...patch };
  const next = mode === "resize" ? clampResize(merged, bounds) : clampMove(merged, bounds);
  if (!isInsideGrid(next, bounds)) return null;
  const others = (peers || items || []).filter((item) => item.id !== id);
  if (others.some((item) => itemsOverlap(next, item))) return null;
  return (items || []).map((item) => (item.id === id ? next : item));
}

/**
 * Walks toward the target one cell at a time and keeps the last valid step, so
 * dragging into a neighbour slides up against it instead of snapping back.
 */
function walkChange(items, id, targetPatch, options, mode) {
  const current = findItem(items, id);
  if (!current) return null;
  const merged = { ...current, ...targetPatch };
  const target = mode === "resize" ? clampResize(merged, options.bounds) : clampMove(merged, options.bounds);
  const posKeys = mode === "resize" ? ["width", "height"] : ["col", "row"];
  const dCol = target[posKeys[0]] - current[posKeys[0]];
  const dRow = target[posKeys[1]] - current[posKeys[1]];
  const steps = Math.max(Math.abs(dCol), Math.abs(dRow));
  if (steps === 0) return items;
  let best = null;
  for (let step = 1; step <= steps; step += 1) {
    const patch = {
      [posKeys[0]]: current[posKeys[0]] + Math.round((dCol * step) / steps),
      [posKeys[1]]: current[posKeys[1]] + Math.round((dRow * step) / steps),
    };
    const next = tryUpdateItem(items, id, patch, { ...options, mode });
    if (!next) break;
    best = next;
  }
  return best;
}

export function tryMoveItem(items, id, col, row, options) {
  return walkChange(items, id, { col, row }, options, "move");
}

export function tryResizeItem(items, id, width, height, options) {
  return walkChange(items, id, { width, height }, options, "resize");
}

function occupancyGrid(items, bounds) {
  const occupied = Array.from({ length: bounds.rows }, () => Array(bounds.columns).fill(false));
  (items || []).forEach((item) => {
    if (!isInsideGrid(item, bounds)) return;
    for (let row = item.row; row < item.row + item.height; row += 1) {
      for (let col = item.col; col < item.col + item.width; col += 1) {
        occupied[row - 1][col - 1] = true;
      }
    }
  });
  return occupied;
}

/**
 * First free cell in row-major order, at the preferred size or whatever
 * rectangle actually fits there. Null when nothing fits anywhere.
 */
export function findFirstAvailablePlacement(items, bounds, preferredWidth, preferredHeight) {
  const occupied = occupancyGrid(items, bounds);
  const wantWidth = Math.max(MIN_ITEM_SIZE, asInt(preferredWidth, MIN_ITEM_SIZE));
  const wantHeight = Math.max(MIN_ITEM_SIZE, asInt(preferredHeight, MIN_ITEM_SIZE));
  for (let row = 1; row <= bounds.rows; row += 1) {
    for (let col = 1; col <= bounds.columns; col += 1) {
      if (occupied[row - 1][col - 1]) continue;
      let maxWidth = 0;
      for (let nextCol = col; nextCol <= bounds.columns; nextCol += 1) {
        if (occupied[row - 1][nextCol - 1]) break;
        maxWidth += 1;
      }
      const width = Math.min(wantWidth, maxWidth);
      if (width < MIN_ITEM_SIZE) continue;
      let maxHeight = 0;
      heightLoop: for (let nextRow = row; nextRow <= bounds.rows; nextRow += 1) {
        for (let nextCol = col; nextCol < col + width; nextCol += 1) {
          if (occupied[nextRow - 1][nextCol - 1]) break heightLoop;
        }
        maxHeight += 1;
      }
      if (maxHeight < MIN_ITEM_SIZE) continue;
      return { col, row, width, height: Math.min(wantHeight, maxHeight) };
    }
  }
  return null;
}

/** Pointer travel in whole cells. Rounding is what makes a drag snap. */
export function cellDeltaFromPixels(dx, dy, cellWidth, cellHeight) {
  const width = Number(cellWidth) || 0;
  const height = Number(cellHeight) || 0;
  return {
    col: width ? Math.round(Number(dx) / width) : 0,
    row: height ? Math.round(Number(dy) / height) : 0,
  };
}

export function cellSizeFromRect(rect, bounds) {
  const width = Number(rect?.width) || 0;
  const height = Number(rect?.height) || 0;
  return {
    cellWidth: bounds.columns > 0 ? width / bounds.columns : 0,
    cellHeight: bounds.rows > 0 ? height / bounds.rows : 0,
  };
}

/** The cell a pointer is over, relative to a grid element's rect. */
export function cellFromPoint(rect, bounds, clientX, clientY) {
  const { cellWidth, cellHeight } = cellSizeFromRect(rect, bounds);
  const col = cellWidth ? Math.floor((Number(clientX) - (rect.left || 0)) / cellWidth) + 1 : 1;
  const row = cellHeight ? Math.floor((Number(clientY) - (rect.top || 0)) / cellHeight) + 1 : 1;
  return {
    col: clampInt(col, 1, bounds.columns),
    row: clampInt(row, 1, bounds.rows),
  };
}

/**
 * Multi-item geometry — the Page Designer card movement/alignment contract
 * with the bounds made a parameter. Form Designer cards use the same
 * functions on the form canvas. Only col/row/width/height change; there is
 * no second coordinate system.
 */

/** Resolves ids to items, or null when the group doesn't meet `minCount`. */
export function selectedItems(items, ids, minCount = 1) {
  const uniqueIds = Array.from(new Set(ids || []));
  if (uniqueIds.length < minCount) return null;
  const selected = uniqueIds.map((id) => findItem(items, id));
  if (selected.some((item) => !item)) return null;
  return selected;
}

/**
 * Moves a group to explicit absolute cell targets at once, atomically:
 * every listed item lands, or none do. Used when dragging 2+ selected
 * items together so their relative arrangement cannot deform.
 */
export function tryMoveItemsTo(items, moves, bounds) {
  const list = Array.isArray(moves) ? moves : [];
  if (list.length === 0) return null;
  const patches = new Map();
  for (const { id, col, row } of list) {
    const current = findItem(items, id);
    if (!current) return null;
    const nextItem = { ...current, col: asInt(col, current.col), row: asInt(row, current.row) };
    if (!isInsideGrid(nextItem, bounds)) return null;
    patches.set(id, nextItem);
  }
  const candidate = (items || []).map((item) => patches.get(item.id) || item);
  if (hasOverlap(candidate)) return null;
  return candidate;
}

/** Moves every selected item by the same whole-cell delta. */
export function nudgeItemsCells(items, ids, dCol, dRow, bounds) {
  const selected = selectedItems(items, ids, 1);
  if (!selected) return null;
  const col = asInt(dCol, 0);
  const row = asInt(dRow, 0);
  return tryMoveItemsTo(
    items,
    selected.map((item) => ({ id: item.id, col: item.col + col, row: item.row + row })),
    bounds,
  );
}

export const ALIGN_LEFT = "left";
export const ALIGN_RIGHT = "right";
export const ALIGN_TOP = "top";
export const ALIGN_BOTTOM = "bottom";
export const ALIGN_CENTER_HORIZONTAL = "center-horizontal";
export const ALIGN_CENTER_VERTICAL = "center-vertical";

/**
 * Grid-cell alignment. Target edges/centers come from the selection's own
 * bounding box, then every selected item moves to a whole cell. Validated
 * against the whole resulting layout and rejected atomically.
 */
export function alignItems(items, ids, edge, bounds) {
  const selected = selectedItems(items, ids, 2);
  if (!selected) return null;

  const left = Math.min(...selected.map((item) => item.col));
  const right = Math.max(...selected.map((item) => item.col + item.width));
  const top = Math.min(...selected.map((item) => item.row));
  const bottom = Math.max(...selected.map((item) => item.row + item.height));

  const patches = new Map();
  for (const item of selected) {
    let col = item.col;
    let row = item.row;
    switch (edge) {
      case ALIGN_LEFT:
        col = left;
        break;
      case ALIGN_RIGHT:
        col = right - item.width;
        break;
      case ALIGN_TOP:
        row = top;
        break;
      case ALIGN_BOTTOM:
        row = bottom - item.height;
        break;
      case ALIGN_CENTER_HORIZONTAL:
        col = Math.round((left + right) / 2 - item.width / 2);
        break;
      case ALIGN_CENTER_VERTICAL:
        row = Math.round((top + bottom) / 2 - item.height / 2);
        break;
      default:
        return null;
    }
    const nextItem = { ...item, col, row };
    if (!isInsideGrid(nextItem, bounds)) return null;
    patches.set(item.id, nextItem);
  }

  const candidate = (items || []).map((item) => patches.get(item.id) || item);
  if (hasOverlap(candidate)) return null;
  return candidate;
}

export function alignItemsLeft(items, ids, bounds) {
  return alignItems(items, ids, ALIGN_LEFT, bounds);
}

export function alignItemsRight(items, ids, bounds) {
  return alignItems(items, ids, ALIGN_RIGHT, bounds);
}

export function alignItemsTop(items, ids, bounds) {
  return alignItems(items, ids, ALIGN_TOP, bounds);
}

export function alignItemsBottom(items, ids, bounds) {
  return alignItems(items, ids, ALIGN_BOTTOM, bounds);
}

export function alignItemsCenterHorizontal(items, ids, bounds) {
  return alignItems(items, ids, ALIGN_CENTER_HORIZONTAL, bounds);
}

export function alignItemsCenterVertical(items, ids, bounds) {
  return alignItems(items, ids, ALIGN_CENTER_VERTICAL, bounds);
}

export const DISTRIBUTE_HORIZONTAL = "horizontal";
export const DISTRIBUTE_VERTICAL = "vertical";

function distributeItems(items, ids, axis, bounds) {
  const selected = selectedItems(items, ids, 3);
  if (!selected) return null;
  const posKey = axis === DISTRIBUTE_HORIZONTAL ? "col" : "row";
  const sizeKey = axis === DISTRIBUTE_HORIZONTAL ? "width" : "height";

  const sorted = [...selected].sort((a, b) => a[posKey] - b[posKey]);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const span = last[posKey] + last[sizeKey] - first[posKey];
  const sumSizes = sorted.reduce((total, item) => total + item[sizeKey], 0);
  const gap = (span - sumSizes) / (sorted.length - 1);

  const patches = new Map();
  let cursor = first[posKey] + first[sizeKey];
  for (let i = 1; i < sorted.length - 1; i += 1) {
    const item = sorted[i];
    cursor += gap;
    const pos = Math.round(cursor);
    const nextItem = { ...item, [posKey]: pos };
    if (!isInsideGrid(nextItem, bounds)) return null;
    patches.set(item.id, nextItem);
    cursor = pos + item[sizeKey];
  }

  const candidate = (items || []).map((item) => patches.get(item.id) || item);
  if (hasOverlap(candidate)) return null;
  return candidate;
}

export function distributeItemsHorizontally(items, ids, bounds) {
  return distributeItems(items, ids, DISTRIBUTE_HORIZONTAL, bounds);
}

export function distributeItemsVertically(items, ids, bounds) {
  return distributeItems(items, ids, DISTRIBUTE_VERTICAL, bounds);
}

function matchItemsDimensions(items, ids, dims, bounds) {
  const selected = selectedItems(items, ids, 2);
  if (!selected) return null;
  const patches = new Map();
  for (const item of selected) {
    const width = dims.width ?? item.width;
    const height = dims.height ?? item.height;
    const nextItem = { ...item, width, height };
    if (!isInsideGrid(nextItem, bounds)) return null;
    patches.set(item.id, nextItem);
  }
  const candidate = (items || []).map((item) => patches.get(item.id) || item);
  if (hasOverlap(candidate)) return null;
  return candidate;
}

export function matchItemsWidth(items, ids, referenceId, bounds) {
  const reference = findItem(items, referenceId);
  if (!reference) return null;
  return matchItemsDimensions(items, ids, { width: reference.width }, bounds);
}

export function matchItemsHeight(items, ids, referenceId, bounds) {
  const reference = findItem(items, referenceId);
  if (!reference) return null;
  return matchItemsDimensions(items, ids, { height: reference.height }, bounds);
}

export function matchItemsSize(items, ids, referenceId, bounds) {
  const reference = findItem(items, referenceId);
  if (!reference) return null;
  return matchItemsDimensions(items, ids, { width: reference.width, height: reference.height }, bounds);
}
